import { useCallback } from 'react';
import { useGetBlockedSlotsQuery } from '../api';
import type { BlockedSlot } from '../api/types';

type UseBlockedSlotsParams = {
  dateFrom: string;
  dateTo: string;
  opportunityId?: string;
};

function toLocalDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Hook to check if a date or time slot is blocked.
 *
 * Loads blocked_slots for the given date range and provides
 * two helpers: isBlockedDay and isBlockedTime.
 *
 * Blocked slots with opportunity_id = null apply globally.
 * Blocked slots with a specific opportunity_id apply only to that opportunity.
 */
export function useBlockedSlots({ dateFrom, dateTo, opportunityId }: UseBlockedSlotsParams) {
  const { data: blockedSlots = [], isLoading } = useGetBlockedSlotsQuery({
    date_from: dateFrom,
    date_to: dateTo,
    opportunity_id: opportunityId,
  });

  /**
   * Check if an entire day is blocked (all_day = true)
   */
  const isBlockedDay = useCallback(
    (date: Date): boolean => {
      const dateStr = toLocalDateStr(date);
      return blockedSlots.some((slot: BlockedSlot) => slot.date === dateStr && slot.all_day);
    },
    [blockedSlots]
  );

  /**
   * Check if a specific hour on a date is blocked.
   * Checks both all_day blocks and time-range blocks.
   */
  const isBlockedTime = useCallback(
    (date: Date, hour: string): boolean => {
      const dateStr = toLocalDateStr(date);
      const hourNum = parseInt(hour.split(':')[0], 10);

      return blockedSlots.some((slot: BlockedSlot) => {
        if (slot.date !== dateStr) return false;

        // All day block
        if (slot.all_day) return true;

        // Time range block
        if (slot.start_time && slot.end_time) {
          const blockStart = parseInt(slot.start_time.split(':')[0], 10);
          const blockEnd = parseInt(slot.end_time.split(':')[0], 10);
          return hourNum >= blockStart && hourNum < blockEnd;
        }

        return false;
      });
    },
    [blockedSlots]
  );

  /**
   * Get the reason for a blocked day or time (for tooltips)
   */
  const getBlockReason = useCallback(
    (date: Date, hour?: string): string | null => {
      const dateStr = toLocalDateStr(date);

      const block = blockedSlots.find((slot: BlockedSlot) => {
        if (slot.date !== dateStr) return false;
        if (slot.all_day) return true;
        if (hour && slot.start_time && slot.end_time) {
          const hourNum = parseInt(hour.split(':')[0], 10);
          const blockStart = parseInt(slot.start_time.split(':')[0], 10);
          const blockEnd = parseInt(slot.end_time.split(':')[0], 10);
          return hourNum >= blockStart && hourNum < blockEnd;
        }
        return false;
      });

      return block?.reason || null;
    },
    [blockedSlots]
  );

  return {
    blockedSlots,
    isLoading,
    isBlockedDay,
    isBlockedTime,
    getBlockReason,
  };
}
